import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MapPin, Clock, Calendar, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import Loader from '@/components/Loader';
import { useGeolocation } from '@/hooks/use-geolocation';
import { getDeviceId } from '@/utils/deviceId';

interface Meeting {
  _id: string;
  title: string;
  datetime: string;
  meeting_type: string;
  clearance_stage: 'first' | 'second' | 'final';
  location: string;
  location_lat: number;
  location_lng: number;
  radius: number;
  admin_name: string;
}

const MembersAttendancePage: React.FC = () => {
  const { adminId } = useParams<{ adminId: string }>();
  const [loading, setLoading] = useState(true);
  const [showLoader, setShowLoader] = useState(true);
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [formData, setFormData] = useState({
    full_name: '',
    state_code: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [locationVerified, setLocationVerified] = useState(false);
  const [locationAttempts, setLocationAttempts] = useState(0);
  const [distance, setDistance] = useState<number | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [locationAccuracy, setLocationAccuracy] = useState<number | null>(null);
  const [attendanceSubmissions, setAttendanceSubmissions] = useState<any[]>([]);

  const { 
    position,
    error: locationError,
    loading: locationLoading,
    permissionStatus,
    requestGeolocation
  } = useGeolocation();

  // Add function to fetch attendance submissions
  const fetchAttendanceSubmissions = async (meetingId: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/${adminId}/attendance-submissions`);
      if (!response.ok) {
        throw new Error('Failed to fetch attendance submissions');
      }
      const data = await response.json();
      // Filter submissions for current meeting
      const meetingSubmissions = data.filter((submission: any) => submission.meeting_id === meetingId);
      setAttendanceSubmissions(meetingSubmissions);
    } catch (error) {
      console.error('Error fetching attendance submissions:', error);
    }
  };

  useEffect(() => {
    fetchLatestMeeting();
  }, [adminId]);

  const fetchLatestMeeting = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/${adminId}/latest-meeting`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch meeting details');
      }

      const data = await response.json();
      
      // Validate meeting data
      if (!data || !data._id || !data.clearance_stage) {
        throw new Error('Invalid meeting configuration: Missing required fields');
      }

      // Ensure clearance_stage is one of the valid values
      if (!['first', 'second', 'final'].includes(data.clearance_stage)) {
        throw new Error('Invalid meeting configuration: Invalid clearance stage');
      }

      setMeeting(data);
      // Fetch attendance submissions for this meeting
      await fetchAttendanceSubmissions(data._id);
    } catch (error) {
      console.error('Error fetching meeting:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to load meeting details');
    } finally {
      setLoading(false);
    }
  };

  // Function to calculate distance between two points using the Haversine formula
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  };

  // Function to verify location
  const verifyLocation = async () => {
    if (isVerifying) return;
    
    setIsVerifying(true);
    setLocationVerified(false);
    
    try {
      if (!meeting) {
        toast.error('No active meeting found');
        return;
      }

      // For iPhone, we need to be more patient and collect multiple readings
      const locationReadings: Array<{lat: number, lng: number, accuracy: number}> = [];
      const maxReadings = 3;
      const readingInterval = 2000; // 2 seconds between readings

      for (let i = 0; i < maxReadings; i++) {
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            const timeoutId = setTimeout(() => {
              reject(new Error('Location request timed out'));
            }, 30000); // 30 second timeout for each reading

            navigator.geolocation.getCurrentPosition(
              (pos) => {
                clearTimeout(timeoutId);
                resolve(pos);
              },
              (error) => {
                clearTimeout(timeoutId);
                reject(error);
              },
              {
                enableHighAccuracy: true,
                timeout: 25000, // 25 second timeout
                maximumAge: 10000 // Allow 10 second old readings for better performance
              }
            );
          });

          // Store the reading
          locationReadings.push({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy || 0
          });

          // Update accuracy display
          setLocationAccuracy(position.coords.accuracy);

          // If we have enough readings, calculate average
          if (locationReadings.length >= 2) {
            const avgLat = locationReadings.reduce((sum, reading) => sum + reading.lat, 0) / locationReadings.length;
            const avgLng = locationReadings.reduce((sum, reading) => sum + reading.lng, 0) / locationReadings.length;
            
            // Calculate distance using average position
            const currentDistance = calculateDistance(
              avgLat,
              avgLng,
              meeting.location_lat,
              meeting.location_lng
            );

            setDistance(currentDistance);

            // For iPhone, we need to be more lenient with the radius check
            // Add a buffer based on the accuracy of the GPS reading
            const accuracyBuffer = Math.max(locationReadings[locationReadings.length - 1].accuracy, 20); // Minimum 20m buffer
            const effectiveRadius = meeting.radius + accuracyBuffer;

            if (currentDistance <= effectiveRadius) {
              setLocationVerified(true);
              toast.success(`Location verified! You are ${Math.round(currentDistance)}m from the meeting location.`, {
                duration: 4000, // Show for 4 seconds
              });
              break; // Exit the loop if we have a good reading
            } else if (i === maxReadings - 1) {
              // Last attempt
              setLocationVerified(false);
              toast.error(`You are ${Math.round(currentDistance)}m away from the meeting location. Please move closer.`, {
                duration: 5000, // Show for 5 seconds
              });
              setLocationAttempts(prev => prev + 1);
            }
          }

          // Wait before next reading (except for the last one)
          if (i < maxReadings - 1) {
            await new Promise(resolve => setTimeout(resolve, readingInterval));
          }

        } catch (readingError) {
          console.error(`Reading ${i + 1} failed:`, readingError);
          
          // If it's the last reading and we have some data, try with what we have
          if (i === maxReadings - 1 && locationReadings.length > 0) {
            const avgLat = locationReadings.reduce((sum, reading) => sum + reading.lat, 0) / locationReadings.length;
            const avgLng = locationReadings.reduce((sum, reading) => sum + reading.lng, 0) / locationReadings.length;
            
            const currentDistance = calculateDistance(
              avgLat,
              avgLng,
              meeting.location_lat,
              meeting.location_lng
            );

            setDistance(currentDistance);
            
            // Be more lenient with partial readings
            const accuracyBuffer = 50; // Larger buffer for partial readings
            const effectiveRadius = meeting.radius + accuracyBuffer;

            if (currentDistance <= effectiveRadius) {
              setLocationVerified(true);
              toast.success(`Location verified! You are ${Math.round(currentDistance)}m from the meeting location.`);
            } else {
              setLocationVerified(false);
              toast.error(`You are ${Math.round(currentDistance)}m away from the meeting location. Please move closer.`);
              setLocationAttempts(prev => prev + 1);
            }
          }
        }
      }

    } catch (error) {
      console.error('Error getting location:', error);
      setLocationVerified(false);
      
      if (error instanceof GeolocationPositionError) {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error('Location permission denied. Please enable location access in your browser settings and refresh the page.');
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error('Location information is unavailable. Please check your GPS settings and try again.');
            break;
          case error.TIMEOUT:
            toast.error('Location request timed out. Please try again in a few seconds.');
            break;
          default:
            toast.error('Unable to get your location. Please try again.');
        }
      } else if (error instanceof Error && error.message.includes('timed out')) {
        toast.error('Location request timed out. Please try again.');
      } else {
        toast.error('Error getting location. Please try again.');
      }
    } finally {
      setIsVerifying(false);
    }
  };

  // Effect to check location when position changes
  useEffect(() => {
    if (position && meeting && !locationVerified) {
      const currentDistance = calculateDistance(
        position.latitude,
        position.longitude,
        meeting.location_lat,
        meeting.location_lng
      );

      setDistance(currentDistance);

      // For automatic verification, be more lenient with a default accuracy buffer
      const accuracyBuffer = 30; // Default 30m buffer for automatic verification
      const effectiveRadius = meeting.radius + accuracyBuffer;

      if (currentDistance <= effectiveRadius) {
        setLocationVerified(true);
        toast.success(`Location verified! You are ${Math.round(currentDistance)}m from the meeting location.`, {
          duration: 4000, // Show for 4 seconds
        });
      }
    }
  }, [position, meeting, locationVerified]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!locationVerified) {
      toast.error('Please verify your location first');
      return;
    }

    if (!formData.full_name.trim()) {
      toast.error('Please enter your full name');
      return;
    }

    if (!formData.state_code.trim()) {
      toast.error('Please enter your state code');
      return;
    }

    if (!meeting) {
      toast.error('No active meeting found');
      return;
    }

    if (!meeting._id || !meeting.clearance_stage) {
      toast.error('Invalid meeting configuration: Please contact the administrator');
      return;
    }

    try {
      console.log('Checking for duplicate submissions...');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/${adminId}/attendance-submissions`);
      if (!response.ok) {
        throw new Error(`Failed to check attendance submissions: ${response.status} ${response.statusText}`);
      }
      const submissions = await response.json();
      
      // Get device ID before checking
      const deviceId = getDeviceId(meeting._id, formData.state_code.trim());
      
      // Check if this state code has used a different device
      const stateCodeDevice = submissions.find(
        (submission: any) => 
          submission.meeting_id === meeting._id && 
          submission.state_code.toLowerCase() === formData.state_code.toLowerCase() &&
          submission.device_id !== deviceId
      );

      if (stateCodeDevice) {
        toast.error('This state code has already been used with a different device for this meeting');
        return;
      }

      // Check if this device has been used by a different state code
      const deviceStateCode = submissions.find(
        (submission: any) => 
          submission.meeting_id === meeting._id && 
          submission.device_id === deviceId &&
          submission.state_code.toLowerCase() !== formData.state_code.toLowerCase()
      );

      if (deviceStateCode) {
        toast.error('This device has already been used by another state code for this meeting');
        return;
      }

      // Check for duplicate submission
      const isDuplicate = submissions.some(
        (submission: any) => 
          submission.meeting_id === meeting._id && 
          submission.state_code.toLowerCase() === formData.state_code.toLowerCase()
      );

      if (isDuplicate) {
        toast.error('This state code has already been used for attendance in this meeting');
        return;
      }

      setSubmitting(true);

      console.log('Getting current position...');
      // Get current position before submitting
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        });
      });

      const submitResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/attendance/mark`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          meeting_id: meeting._id,
          admin_id: adminId,
          full_name: formData.full_name.trim(),
          state_code: formData.state_code.trim(),
          member_latitude: position.coords.latitude,
          member_longitude: position.coords.longitude,
          timestamp: new Date().toISOString(),
          clearance_stage: meeting.clearance_stage,
          device_id: deviceId
        })
      });

      if (!submitResponse.ok) {
        const error = await submitResponse.json();
        console.error('Server response:', error);
        if (error.code === 'DEVICE_ALREADY_USED') {
          toast.error('❌ This device has already been used by another state code for this meeting.');
        } else if (error.code === 'STATE_CODE_DEVICE_MISMATCH') {
          toast.error('❌ This state code has already been used with a different device for this meeting.');
        } else if (error.code === 'LOCATION_ALREADY_USED') {
          toast.error('❌ This location has already been used for this meeting. Please move to a different location.');
        } else if (error.code === 'NEARBY_LOCATION_USED') {
          toast.error('❌ You have already marked attendance from a nearby location. Please move further away.');
        } else {
          throw new Error(`Server error: ${error.message || 'Failed to mark attendance'}`);
        }
        return;
      }

      // Show success message
      toast.success('Attendance marked successfully! You can now close this page.');
      
      // Clear form and reset state
      setFormData({ full_name: '', state_code: '' });
      setLocationVerified(false);
      
      // Reload the page after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error('Detailed error:', error);
      if (error instanceof GeolocationPositionError) {
        toast.error(`Location error: ${error.message}`);
      } else if (error instanceof Error) {
        toast.error(`Error: ${error.message}`);
      } else {
        toast.error('An unexpected error occurred. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-gray-500">No active meeting found</p>
      </div>
    );
  }
   if (loading) {
      return <Loader />;
    }
  
    if (showLoader) {
      return <Loader duration={3000} onComplete={() => setShowLoader(false)} />;
    }
  

  return (
    <div className="container max-w-3xl py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-2xl">Mark Attendance</CardTitle>
            <CardDescription>Verify your presence and submit your attendance</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Location Verification Section */}
              <div className="space-y-4">
                <Label>Location Verification</Label>
                <div className="flex flex-col space-y-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={verifyLocation}
                    disabled={isVerifying || locationLoading}
                    className="w-full"
                  >
                    {isVerifying ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Verifying Location... (Multiple readings)
                      </>
                    ) : locationLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Getting Location...
                      </>
                    ) : locationVerified ? (
                      <>
                        <MapPin className="h-4 w-4 mr-2" />
                        Location Verified ✓
                      </>
                    ) : permissionStatus === 'denied' ? (
                      <>
                        <MapPin className="h-4 w-4 mr-2" />
                        Location Access Denied
                      </>
                    ) : (
                      <>
                        <MapPin className="h-4 w-4 mr-2" />
                        Verify Location
                      </>
                    )}
                  </Button>
                  
                  {permissionStatus === 'denied' && (
                    <p className="text-sm text-red-600">
                      Please enable location access in your browser settings to mark attendance.
                    </p>
                  )}
                  
                  {distance !== null && (
                    <div className="space-y-1">
                      <p className={`text-sm ${locationVerified ? 'text-green-600' : 'text-amber-600'}`}>
                        Current distance: {Math.round(distance)}m from meeting location
                      </p>
                      {locationAccuracy !== null && (
                        <p className="text-xs text-gray-500">
                          GPS Accuracy: ±{Math.round(locationAccuracy)}m
                        </p>
                      )}
                    </div>
                  )}
                  
                  {locationAttempts > 0 && !locationVerified && (
                    <p className="text-sm text-amber-600">
                      Location verification attempts: {locationAttempts}
                    </p>
                  )}

                  {!locationVerified && (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600">
                        Please make sure you are at the meeting location and have enabled location services.
                      </p>
                      <p className="text-xs text-gray-500">
                        <strong>iPhone users:</strong> Ensure "Precise Location" is enabled in Settings → Privacy → Location Services → Safari/Chrome → Precise Location.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Attendance Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                    required
                    disabled={!locationVerified}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="state_code">State Code</Label>
                  <Input
                    id="state_code"
                    value={formData.state_code}
                    onChange={(e) => setFormData(prev => ({ ...prev, state_code: e.target.value }))}
                    required
                    disabled={!locationVerified}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={!locationVerified || submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Attendance'
                  )}
                </Button>
              </form>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default MembersAttendancePage; 